/**
 * Audience Performance Modal
 * Story: E007-S027 Audience Manager & Targeting Optimization
 */

import React from 'react';
import { useAudiencePerformance, useAudienceRecommendations } from '../../lib/audiencesApi';
import type { Audience, Recommendation } from '../../types/audiences';
import { formatAudienceSize, formatROAS, formatCTR, formatCurrency, PLATFORM_INFO, STATUS_INFO } from '../../types/audiences';

interface Props {
  audience: Audience;
  onClose: () => void;
}

const RecommendationCard: React.FC<{ rec: Recommendation }> = ({ rec }) => {
  const priorityColors: Record<string, string> = {
    high: 'border-red-500 bg-red-50 dark:bg-red-900/20',
    medium: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
    low: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
  };

  return (
    <div className={`border-l-4 ${priorityColors[rec.priority]} p-3 rounded-r-lg`}>
      <div className="flex items-start justify-between">
        <div>
          <span className="text-xs font-medium uppercase text-gray-500">{rec.type}</span>
          <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">{rec.message}</p>
        </div>
        <span className={`px-2 py-0.5 text-xs rounded-full ${
          rec.priority === 'high' ? 'bg-red-100 text-red-700' :
          rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
          'bg-blue-100 text-blue-700'
        }`}>
          {rec.priority}
        </span>
      </div>
      <button className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium">
        {rec.action} →
      </button>
    </div>
  );
};

export const AudiencePerformanceModal: React.FC<Props> = ({ audience, onClose }) => {
  const { data: performanceData, isLoading } = useAudiencePerformance(audience.id);
  const { data: recommendations } = useAudienceRecommendations(audience.id);

  const metrics = performanceData?.metrics?.totals;
  const daily = performanceData?.metrics?.daily || [];
  const campaigns = performanceData?.metrics?.campaigns_using || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              {audience.name}
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                PLATFORM_INFO[audience.platform].color === 'blue' ? 'bg-blue-100 text-blue-700' :
                PLATFORM_INFO[audience.platform].color === 'red' ? 'bg-red-100 text-red-700' :
                'bg-pink-100 text-pink-700'
              }`}>
                {PLATFORM_INFO[audience.platform].name}
              </span>
            </h2>
            <p className="text-sm text-gray-500 mt-1">{audience.description}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatAudienceSize(audience.size)}
              </div>
              <div className="text-sm text-gray-500">Audience Size</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">
                {metrics ? formatROAS(metrics.roas) : '-'}
              </div>
              <div className="text-sm text-gray-500">ROAS (30d)</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">
                {metrics ? formatCTR(metrics.ctr) : '-'}
              </div>
              <div className="text-sm text-gray-500">CTR</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {metrics ? formatCurrency(metrics.spend) : '-'}
              </div>
              <div className="text-sm text-gray-500">Spend (30d)</div>
            </div>
          </div>

          {/* Performance Details */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500">Impressions</span>
              <span className="font-medium">{metrics?.impressions?.toLocaleString() || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500">Clicks</span>
              <span className="font-medium">{metrics?.clicks?.toLocaleString() || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500">Conversions</span>
              <span className="font-medium">{metrics?.conversions?.toLocaleString() || '-'}</span>
            </div>
          </div>

          {/* Recommendations */}
          {recommendations && recommendations.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Targeting Recommendations
              </h3>
              <div className="space-y-3">
                {recommendations.map((rec, idx) => (
                  <RecommendationCard key={idx} rec={rec} />
                ))}
              </div>
            </div>
          )}

          {/* Campaigns Using */}
          {campaigns.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Campaigns Using This Audience ({campaigns.length})
              </h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr>
                      <th className="text-left px-4 py-2 text-gray-500">Campaign</th>
                      <th className="text-right px-4 py-2 text-gray-500">Impressions</th>
                      <th className="text-right px-4 py-2 text-gray-500">Spend</th>
                      <th className="text-right px-4 py-2 text-gray-500">ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {campaigns.slice(0, 5).map((c) => (
                      <tr key={c.campaign_id}>
                        <td className="px-4 py-2">{c.campaign_name}</td>
                        <td className="px-4 py-2 text-right">{c.impressions?.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(c.spend)}</td>
                        <td className="px-4 py-2 text-right text-green-600">{formatROAS(c.roas)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Audience Criteria */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Targeting Criteria
            </h3>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-3">
              {audience.criteria?.demographics && (
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Demographics</span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {audience.criteria.demographics.age_min && (
                      <span className="px-2 py-1 bg-white dark:bg-gray-800 rounded text-sm">
                        Ages {audience.criteria.demographics.age_min}-{audience.criteria.demographics.age_max}
                      </span>
                    )}
                    {audience.criteria.demographics.locations?.map((loc) => (
                      <span key={loc} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-sm">
                        📍 {loc}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {audience.criteria?.interests && audience.criteria.interests.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Interests</span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {audience.criteria.interests.map((i) => (
                      <span key={i} className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded text-sm">
                        {i}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
